import cn from 'classnames';

import {
    DEFAULT_FONT_WEIGHT,
    defaultFontSize,
    defaultLineHeight,
    defaultTheme,
    FONT_WEIGHTS_VALID,
    fontPresets,
    fontWeightsMap,
    LINE_HEIGHTS,
} from './config';
import type {
    IDeps,
    IFontPreset,
    IGetClassName,
    ILineHeightConvert,
    IProps,
    TFontSize,
    TFontWeightValid,
    THeight,
    TLineHeight,
    TTextV2Theme,
    TThemeValid,
} from './models';

/**
 * Выбираем line-height из списка разрешенных значений
 * @param lineHeight
 */
export function getLineHeight(lineHeight?: number): TLineHeight {
    // Выбираем минимальный lineHeight
    if (!lineHeight || lineHeight < LINE_HEIGHTS[0]) {
        return LINE_HEIGHTS[0];
    }

    // Выбираем существующий lineHeight

    // jest не видит эту строчку, хотя тест на это место отрабатывает нормально
    // istanbul ignore next
    if (lineHeight in LINE_HEIGHTS) {
        return lineHeight as TLineHeight;
    }

    // Выбираем максимальный lineHeight
    if (lineHeight > LINE_HEIGHTS[LINE_HEIGHTS.length - 1]) {
        return LINE_HEIGHTS[LINE_HEIGHTS.length - 1];
    }

    // Выбираем примерно подходящий lineHeight из списка
    return LINE_HEIGHTS.reduce((prev, curr) =>
        Math.abs(curr - lineHeight) < Math.abs(prev - lineHeight) ? curr : prev
    );
}

/**
 * Функция реализации обратной совместимости для старых значений font-weight
 * @param fontWeight
 */
export function getFontWeight(fontWeight: number): TFontWeightValid {
    if (FONT_WEIGHTS_VALID.includes(fontWeight as TFontWeightValid)) {
        return fontWeight as TFontWeightValid;
    }

    return fontWeightsMap[fontWeight];
}

export function prepareSet({
    fontPreset,
    fontSize,
    fontViews,
    fontWeight,
    lineHeight,
    textTransform,
    view,
}: Pick<IProps, 'fontPreset' | 'view' | 'lineHeight' | 'fontWeight' | 'textTransform' | 'kind'> &
    Pick<IDeps, 'fontViews'> & {
        fontSize: TFontSize;
    }): IFontPreset {
    if (fontPreset) return fontPresets[fontPreset];

    return {
        fontSize,
        ...(view && fontViews[view]),
        ...(fontWeight && { fontWeight: getFontWeight(fontWeight) }),
        ...(lineHeight && { lineHeight: getLineHeight(lineHeight) }),
        ...(textTransform && { textTransform }),
    };
}

// TODO: пока приходится связывать группы тем и приводить только к имеющимся темам в самом text-v2
export const getTheme = (theme: TTextV2Theme): TThemeValid => {
    switch (theme) {
        case 'light':
        case 'white':
            return 'light';
        case 'black':
        case 'dark':
        case 'blue':
            return 'dark';
        default:
            return defaultTheme;
    }
};

/**
 * Высчитываем высоту строки
 * @param lines - количество строк
 * @param lineHeight - высота строки в px или строка по умолчанию в относительных единицах
 * @param fontSize - размер текста
 */
export function calcHeight({ fontSize, lineHeight, lines }: ILineHeightConvert): THeight {
    if (!lines) return undefined;
    if (lineHeight === defaultLineHeight) {
        // webkit, chromium округляет значение в меньшую сторону, тогда как ff ничего не делает.
        // сделаем поведение одинаковым для всех браузеров
        return `${lines * Math.floor(lineHeight * fontSize)}px`;
    }

    return `${lines * lineHeight}px`;
}

/* eslint-disable-next-line sonarjs/cognitive-complexity */
export const getClassNames = (props: IGetClassName): string => {
    const { className, decoration, kind, noClasses, set, styles, textTheme } = props;

    let classNames = '';

    if (!noClasses) {
        classNames = cn(styles.text, {
            [styles[`${textTheme}Theme`]]:
                // Так-то тема нам не нужна, но kind без неё не всегда работает
                textTheme !== defaultTheme || kind,
        });

        if (set.fontSize !== defaultFontSize) {
            classNames = cn(classNames, styles[`fontSize${set.fontSize}`]);
        }

        if (set.fontWeight && set.fontWeight !== DEFAULT_FONT_WEIGHT) {
            classNames = cn(classNames, styles[`fontWeight${set.fontWeight}`]);
        }

        if (set.lineHeight) {
            classNames = cn(classNames, styles[`lineHeight${set.lineHeight}`]);
        }

        if (kind !== undefined) {
            classNames = cn(classNames, styles[`${kind}Kind`]);
        }

        if (set.letterSpacing) {
            classNames = cn(classNames, styles[`letterSpacing${set.letterSpacing}`]);
        }

        if (decoration) {
            classNames = cn(classNames, styles[`${decoration}Decoration`]);
        }

        if (set.textTransform) {
            classNames = cn(classNames, styles[`${set.textTransform}TextTransform`]);
        }
    }

    classNames = cn(classNames, className);

    return classNames;
};
